import { area, centerOfMass } from '@turf/turf';
import { ButtonLinkProps, cn, Stack, toast } from 'component-library';
import { Feature, MultiPolygon, Polygon } from 'geojson';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Marker } from 'react-map-gl';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import { usePostPlotValidateMultiPolygon } from '@/api/rest/resources/plot';
import { Plot } from '@/api/rest/resources/types/plot';
import { PlotTypeIcon } from '@/components';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { DrawControls } from '@/pages/landsteward/pages/plot/components/DrawControls';
import { usePlotForm } from '@/pages/landsteward/pages/plot/hooks/usePlotForm';
import { UnexpectedMissingDataError } from '@/utils/errors/UnexpectedMissingDataError';
import { squareMetersToHectares } from '@/utils/plot';
import { transformPolygonToMultiPolygon } from '@/utils/transformPolygon';

import { useDrawControl } from '../../../../hooks/useDrawControl';
import { LocationSearch } from './components/LocationSearch/LocationSearch';

export type DrawPolygonProps = {
  ForwardButton: (props: Omit<ButtonLinkProps, 'to'>) => JSX.Element;
  BackButton: (props: Omit<ButtonLinkProps, 'to'>) => JSX.Element;
  existingPlots: Plot[];
};

export const DrawPolygon = ({ BackButton, ForwardButton, existingPlots }: DrawPolygonProps) => {
  const { t } = useTranslation();
  const form = usePlotForm();
  const [plotType, plotName, multiPolygon] = form.watch(['plotType', 'name', 'multiPolygon']);

  if (!plotType) {
    throw new UnexpectedMissingDataError({ dataLabel: 'plotType' });
  }

  const validatePolygonMutation = usePostPlotValidateMultiPolygon();

  const validatePolygon = (newFeature: Feature | null) => {
    if (newFeature) {
      validatePolygonMutation.mutate(
        { bodyData: { multi_polygon: newFeature.geometry as MultiPolygon } },
        {
          onError: (error) => {
            const isFormError = !!(error as RestApiBadRequestServerError)?.formErrors?.length;

            if (isFormError) return;

            toast({
              type: 'error',
              title: t('global.ui.toast.errorToastFallbackTitle'),
              description: t('global.ui.toast.errorToastFallbackDescription'),
            });
          },
        },
      );
    }
  };

  const { deleteCurrentPolygon, drawNewPolygon, currentFeature, renderDrawHint } = useDrawControl({
    initialMultiPolygon: multiPolygon,
    plotType,
    onFeatureUpdate: validatePolygon,
  });

  useEffect(
    // eslint-disable-next-line prefer-arrow-callback
    function validateInitialPolygon() {
      if (!multiPolygon) return;
      validatePolygon({ geometry: multiPolygon, type: 'Feature', properties: {} });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [multiPolygon],
  );

  /**
   * Show the location dialog when adding the first
   * ever plot, ie., no existing plots or multiPolygon.
   */
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(!existingPlots.length && !multiPolygon);

  const featureCentroidCoordinates = currentFeature ? centerOfMass(currentFeature).geometry.coordinates : null;

  const areaSquareMeters = currentFeature ? area(currentFeature) : (form.watch('areaSquareMeters') ?? 0);
  const formattedArea = useDisplayNumber(squareMetersToHectares(areaSquareMeters));

  const saveChanges = () => {
    const newMultiPolygon =
      currentFeature?.geometry.type === 'Polygon' // Ensure that polygons are saved as multi-polygons, since this is what the backend expects
        ? transformPolygonToMultiPolygon(currentFeature.geometry as Polygon)
        : (currentFeature?.geometry as MultiPolygon);

    form.setValue('multiPolygon', newMultiPolygon, { shouldDirty: true });
    form.setValue('areaSquareMeters', areaSquareMeters, { shouldDirty: true });
  };

  const validationErrors = (validatePolygonMutation.error as RestApiBadRequestServerError | null)?.formErrors?.reduce(
    (acc, curr) => {
      const result = [...acc];
      curr.messages.forEach((message) => result.push(message));

      return result;
    },
    [] as string[],
  );

  const hasValidationErrors = validatePolygonMutation.isError;
  const hasNoPolygon = !currentFeature;
  const disableSave = hasValidationErrors || hasNoPolygon;

  return (
    <>
      <DrawControls
        onDrawClick={drawNewPolygon}
        onDeleteClick={() => {
          deleteCurrentPolygon();
          validatePolygonMutation.reset();
        }}
        onLocationSearchClick={() => setIsLocationDialogOpen(true)}
        disableDelete={!currentFeature}
        disableDraw={!!currentFeature}
      />

      <LocationSearch open={isLocationDialogOpen} onOpenChange={(o) => setIsLocationDialogOpen(o)} />

      {renderDrawHint()}

      {featureCentroidCoordinates && (
        <Marker longitude={featureCentroidCoordinates[0]} latitude={featureCentroidCoordinates[1]}>
          <Stack className='items-center'>
            <PlotTypeIcon type={plotType} className='text-white-100' />
            {plotName && <span className='typography-caption text-white-100'>{plotName}</span>}
          </Stack>
        </Marker>
      )}

      <Stack
        data-testid='plot-info'
        direction='row'
        className={cn(
          'absolute bottom-10 left-0 right-0 mx-5 my-0 bg-white-100 p-2 pl-6 sm:mx-auto sm:w-[414px]',
          validationErrors ? 'rounded-3xl' : 'rounded-full',
        )}
      >
        <Stack direction='row' className='flex-1 items-center justify-between'>
          <Stack>
            <Stack direction='row' spacing={2} className='items-center'>
              <PlotTypeIcon type={plotType} />
              <span className={cn('typography-h3', validationErrors?.length ? 'text-error' : '')}>
                {formattedArea} ha
              </span>
            </Stack>
            {validationErrors?.map((error, index) => (
              <span key={index} className='typography-caption text-error'>
                {error}
              </span>
            ))}
          </Stack>
          <Stack direction='row' spacing={4}>
            <BackButton data-testid='back-button' />
            <ForwardButton
              disabled={disableSave}
              loading={validatePolygonMutation.isPending}
              onClick={saveChanges}
              data-testid='forward-button'
            />
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};
